/**
 * Date e numeri nella LINGUA DELL'APP. Contratto di fronte `fronti/tma/lingue.md` (KB, I-283),
 * criterio C7.
 *
 * Perche' esiste: `toLocaleString` e `Intl.DateTimeFormat` senza lingua (senza argomenti, o con `undefined`)
 * formattano con la lingua del BROWSER, e la lingua dell'app (da Telegram, da `?lang=`, dalla scelta nelle
 * Preferenze) puo' essere un'altra: un utente con Telegram in greco e il browser in inglese leggeva «1,234» e
 * «Oct 3» in un'app in greco. Misurato sul codice prima di P4: 40 chiamate su 56 non passavano una lingua, e
 * `date-fns` senza `locale` scrive in inglese FISSO. ⇒ una regola ESLint (`eslint.config.js`) vieta la forma
 * senza lingua, e questo e' il posto da cui prenderla.
 *
 * COME SI USA
 *  · in un componente: `const lingua = useLinguaFormato();` (`hooks/useLinguaFormato.ts`) e poi
 *    `formatNumero(n, lingua)`, `formatData(d, opzioni, lingua)`, `localeDateFns(lingua)`. L'hook si iscrive al
 *    cambio di lingua: una lingua letta da qui senza hook non ridisegna il componente quando l'utente la cambia.
 *  · fuori da React (un `lib/` puro, una funzione che riceve la lingua): la lingua arriva come PARAMETRO, come
 *    in `lib/limulo/dateDisplay.ts`; chi non ce l'ha usa `linguaFormato()`.
 *
 * ⚠️ Questo modulo NON importa `@/lib/i18n`: legge l'istanza globale di `i18next` (la stessa su cui `lib/i18n.ts`
 * fa `init`). Niente cataloghi trascinati dentro, niente cicli d'import, e resta importabile dai moduli puri
 * (`lib/limulo/*`) che `tools/verify-*.ts` esercita sotto Node. Se `lib/i18n.ts` non e' ancora stato caricato
 * (un test, uno script) la lingua e' `en`, il ripiego dell'app.
 */
import i18next from 'i18next';
import { el, enUS, es, it } from 'date-fns/locale';

import type { Locale } from 'date-fns';

/** Le locali di `date-fns` per le lingue dell'app: import mirati, non l'intero pacchetto (~100 locali). */
export const LOCALI_DATE_FNS: Readonly<Record<string, Locale>> = { it, en: enUS, es, el };

/** Cio' che serve di un'istanza di i18next per dire la lingua (e che un finto nei test puo' dare). */
export interface IstanzaLingua {
  resolvedLanguage?: string;
  language?: string;
}

/**
 * La lingua con cui scrivere date e numeri: quella dell'app, `en` se i18next non ne ha ancora una.
 * `||` e non `??`: `language` e' `''` prima dell'`init`, e `''` e' una lingua che `Intl` non sa leggere.
 */
export function linguaFormato(istanza?: IstanzaLingua | null): string {
  const i = istanza ?? i18next;
  const lingua = i.resolvedLanguage || i.language || 'en';
  return regionaleDelBrowser(lingua) ?? lingua;
}

/**
 * La lingua dell'app è un codice BASE (`en`), e per `Intl` `en` vuol dire convenzioni USA: 12 ore e
 * mese/giorno. Chi ha l'app in inglese e il browser `en-GB` vedrebbe «06:30 PM · 10/3/2026» invece di
 * «18:30 · 03/10/2026» (rilievo di tma-orc su #544). Quindi, se il browser ha una variante regionale
 * della STESSA lingua, si usa quella: lingua dell'app, convenzioni del paese dell'utente.
 * ⚠️ Il caso che resta, dichiarato: app in inglese (da Telegram) e browser senza nessun `en-*` (es. solo
 * `it-IT`) ⇒ `en` ⇒ convenzioni USA, 12 ore. La lingua di Telegram non porta il paese.
 */
function regionaleDelBrowser(lingua: string): string | undefined {
  const base = lingua.split(/[-_]/)[0].toLowerCase();
  try {
    const nav = (globalThis as { navigator?: { languages?: readonly string[]; language?: string } }).navigator;
    const elenco = nav?.languages?.length ? nav.languages : nav?.language ? [nav.language] : [];
    return elenco.find((tag) => tag.split(/[-_]/)[0].toLowerCase() === base);
  } catch {
    return undefined;
  }
}

/**
 * La locale di `date-fns` per una lingua. La lingua di i18next puo' essere `it-IT`: conta la parte base.
 * Una lingua senza locale (accesa nell'app ma non qui) scrive in inglese, come l'app ripiega sui testi `en`.
 */
export function localeDateFns(lingua: string = linguaFormato()): Locale {
  return LOCALI_DATE_FNS[lingua.split(/[-_]/)[0].toLowerCase()] ?? enUS;
}

/**
 * `useGrouping: 'always'` (ES2023; la `lib` ES2022 di tsconfig lo tipizza solo come booleano): le
 * migliaia si separano anche a 4 cifre. Senza, CLDR (`minimumGroupingDigits` = 2) in it ed es scrive
 * «1500»: il montepremi passava da «5,000 pts» a «5000 pts». Scelta di tma-orc, proprietario della TMA,
 * su #544: «5.000 pts». Un motore che non conosce la stringa la legge come `true`, cioè come prima.
 */
export const RAGGRUPPA_SEMPRE = 'always' as unknown as boolean;

/** Un numero con i separatori della lingua («1.500» e «1.234.567,8» in it, «1,500» e «1,234,567.8» in en). */
export function formatNumero(
  valore: number,
  lingua: string = linguaFormato(),
  opzioni?: Intl.NumberFormatOptions,
): string {
  return valore.toLocaleString(lingua, { useGrouping: RAGGRUPPA_SEMPRE, ...opzioni });
}

type Istante = Date | number | string;

/** La DATA di un istante, nella lingua (`opzioni` come `toLocaleDateString`; senza, la forma numerica della lingua). */
export function formatData(
  istante: Istante,
  opzioni: Intl.DateTimeFormatOptions | undefined,
  lingua: string = linguaFormato(),
): string {
  return new Date(istante).toLocaleDateString(lingua, opzioni);
}

/** L'ORA di un istante, nella lingua (`opzioni` come `toLocaleTimeString`; senza, ore e minuti). */
export function formatOra(
  istante: Istante,
  lingua: string = linguaFormato(),
  opzioni: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' },
): string {
  return new Date(istante).toLocaleTimeString(lingua, opzioni);
}

/** Data e ora insieme, nella lingua (`opzioni` come `toLocaleString`). */
export function formatDataOra(
  istante: Istante,
  opzioni: Intl.DateTimeFormatOptions | undefined,
  lingua: string = linguaFormato(),
): string {
  return new Date(istante).toLocaleString(lingua, opzioni);
}
